import { GtmhubController } from "@gtmhub/core";
import { ICustomField } from "@gtmhub/customFields";
import { copy, IComponentOptions } from "angular";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { ISessionTag } from "@gtmhub/customFields/models/models";
import { QuerySessionsService } from "@gtmhub/customFields/services/query-sessions.service";
import { IPlanningSession } from "@gtmhub/sessions/models/models";

/**
 * Provides functionality for selecting sessions form a session tree view,
 * along with searching for a specific session
 *
 * `custom-field` the custom field for which sessions are being added.
 * `session-tags` session tags.
 *
 * @example
 * <specify-session
 *   custom-field="$ctrl.customField"
 *   session-tags="$ctrl.sessionTags"
 *   cancel="$ctrl.cancel"
 *   done="$ctrl.done"
 * </specify-session>
 */

export interface ISpecifySessionComponentBindings {
  customField: ICustomField;
  sessionTags: ISessionTag[];
  cancel?(): void;
  done?({ selection: ISelection }): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SpecifySessionComponentCtrl extends ISpecifySessionComponentBindings {}
export class SpecifySessionComponentCtrl extends GtmhubController {
  customFieldCopy: ICustomField;
  sessionTagsCopy: ISessionTag[];

  sessionTitleFilter: string;
  filteredSessions: IPlanningSession[];

  selectedIds: string[];

  public static $inject = ["$rootScope", "QuerySessionsService"];

  constructor(private $rootScope: ITraceRootScopeService, private querySessionsService: QuerySessionsService) {
    super();
  }

  $onInit(): void {
    this.customFieldCopy = copy(this.customField);
    this.sessionTagsCopy = copy(this.sessionTags);
  }

  addSessionIdToCustomField(sessionId: string): void {
    this.$rootScope.traceAction("add_session_to_custom_field", () => {
      if (!this.customFieldCopy.targetIds) {
        this.customFieldCopy.targetIds = [];
      }
      if (this.customFieldCopy.targetIds.length == 0) {
        this.customFieldCopy.operationalType = "session";
      }
      this.customFieldCopy.targetIds.push(sessionId);
      if (this.sessionTagsCopy.findIndex((sessionTag) => sessionTag.id === sessionId) < 0) {
        const sessionTag = this.querySessionsService.createSessionTag(sessionId);
        this.sessionTagsCopy.push(sessionTag);
      }
    });
  }

  removeSessionIdFromCustomField(sessionId: string): void {
    this.$rootScope.traceAction("remove_session_from_custom_field", () => {
      this.customFieldCopy.targetIds = this.customFieldCopy.targetIds.filter((session) => {
        return session !== sessionId;
      });

      if (this.customFieldCopy.targetIds.length == 0) {
        this.customFieldCopy.operationalType = "global";
        this.customField.targetIds = [];
      }

      this.sessionTagsCopy.forEach((sessionTag) => {
        if (sessionTag.id === sessionId) {
          this.sessionTagsCopy = this.sessionTagsCopy.filter((sessionTag) => sessionTag.id !== sessionId);
        }
      });
    });
  }

  filterSessionsBySessionTitle(sessionTitleFilter: string): void {
    this.$rootScope.traceAction("search_session_for_custom_field_by_name", () => {
      this.filteredSessions = this.querySessionsService.querySessionsByTitle(sessionTitleFilter);
    });
  }

  selectSessions(): void {
    this.$rootScope.traceAction("select_specified_sessions", () => {
      this.customField = copy(this.customFieldCopy);
      this.sessionTags = copy(this.sessionTagsCopy);

      this.done({ selection: { customField: this.customField, sessionTags: this.sessionTags } });
    });
  }
}

export const SpecifySessionComponent: IComponentOptions = {
  templateUrl: require("./specify-session.jade"),
  controller: SpecifySessionComponentCtrl,
  bindings: {
    customField: "<",
    sessionTags: "<",
    cancel: "&",
    done: "&",
  },
};
